import React, { useState, useMemo } from 'react';
import './GameLogTable.css';

/**
 * GameLogTable Component
 * Displays player's game history with sorting and filtering
 */

const GameLogTable = ({ games }) => {
  const [sortBy, setSortBy] = useState('Date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterResult, setFilterResult] = useState('all');

  // Get unique platforms
  const platforms = useMemo(() => {
    const uniquePlatforms = [...new Set(games.map(g => g.Platform).filter(Boolean))];
    return uniquePlatforms.sort();
  }, [games]);

  // Filter and sort games
  const filteredGames = useMemo(() => {
    let filtered = [...games];

    // Filter by platform
    if (filterPlatform !== 'all') {
      filtered = filtered.filter(g => g.Platform === filterPlatform);
    }

    // Filter by result
    if (filterResult !== 'all') {
      filtered = filtered.filter(g => g.Result === filterResult);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'Date':
          aVal = a.Date || '';
          bVal = b.Date || '';
          break;
        case 'Opponent':
          aVal = a.Opponent || '';
          bVal = b.Opponent || '';
          break;
        case 'Points':
          aVal = parseInt(a.Points_Scored) || 0;
          bVal = parseInt(b.Points_Scored) || 0;
          break;
        case 'Margin':
          aVal = parseInt(a.Point_Margin) || 0;
          bVal = parseInt(b.Point_Margin) || 0;
          break;
        case 'FG%':
          aVal = parseFloat(a.FG_Percentage) || 0;
          bVal = parseFloat(b.FG_Percentage) || 0;
          break;
        default:
          aVal = a.Date || '';
          bVal = b.Date || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [games, sortBy, sortOrder, filterPlatform, filterResult]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getResultClass = (result) => {
    switch (result) {
      case 'W': return 'result-win';
      case 'L': return 'result-loss';
      case 'U': return 'result-undecided';
      default: return '';
    }
  };

  const getResultDisplay = (result) => {
    switch (result) {
      case 'W': return 'WIN';
      case 'L': return 'LOSS';
      case 'U': return 'UNDECIDED';
      default: return result;
    }
  };

  if (games.length === 0) {
    return (
      <div className="game-log-empty">
        <p>No games recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="game-log-container">
      <div className="game-log-header">
        <h2>Game Log</h2>
        <div className="game-log-stats">
          <span className="stat-badge">
            <strong>{games.length}</strong> Total Games
          </span>
          <span className="stat-badge win">
            <strong>{games.filter(g => g.Result === 'W').length}</strong> Wins
          </span>
          <span className="stat-badge loss">
            <strong>{games.filter(g => g.Result === 'L').length}</strong> Losses
          </span>
        </div>
      </div>

      <div className="game-log-filters">
        <div className="filter-group">
          <label>Platform:</label>
          <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
            <option value="all">All Platforms</option>
            {platforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Result:</label>
          <select value={filterResult} onChange={(e) => setFilterResult(e.target.value)}>
            <option value="all">All Results</option>
            <option value="W">Wins</option>
            <option value="L">Losses</option>
            <option value="U">Undecided</option>
          </select>
        </div>

        <div className="filter-info">
          Showing {filteredGames.length} of {games.length} games
        </div>
      </div>

      <div className="game-log-table-wrapper">
        <table className="game-log-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('Date')} className="sortable">
                Date {sortBy === 'Date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('Opponent')} className="sortable">
                Opponent {sortBy === 'Opponent' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Result</th>
              <th>Score</th>
              <th onClick={() => handleSort('Points')} className="sortable">
                PTS {sortBy === 'Points' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('Margin')} className="sortable">
                +/- {sortBy === 'Margin' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('FG%')} className="sortable">
                FG% {sortBy === 'FG%' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Platform</th>
              <th>Type</th>
              <th>Video</th>
            </tr>
          </thead>
          <tbody>
            {filteredGames.map((game, index) => (
              <tr key={game.Game_ID || index}>
                <td>{game.Date || 'N/A'}</td>
                <td className="opponent-name">{game.Opponent || 'Unknown'}</td>
                <td>
                  <span className={`result-badge ${getResultClass(game.Result)}`}>
                    {getResultDisplay(game.Result)}
                  </span>
                </td>
                <td className="score">{game.Final_Score || 'N/A'}</td>
                <td><strong>{game.Points_Scored || 0}</strong></td>
                <td className={parseInt(game.Point_Margin) >= 0 ? 'positive' : 'negative'}>
                  {game.Point_Margin > 0 ? '+' : ''}{game.Point_Margin || 0}
                </td>
                <td>{parseFloat(game.FG_Percentage || 0).toFixed(1)}%</td>
                <td>
                  <span className="platform-badge">{game.Platform || 'N/A'}</span>
                </td>
                <td className="game-type">{game.Game_Type || 'N/A'}</td>
                <td>
                  {game.Video_Link && (
                    <a 
                      href={game.Video_Link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="video-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Watch
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GameLogTable;
